'use strict';

var App = require('app');
var AppState = require('app_state');
var Consts = require('consts');
var Labels = require('models/labels');
var Misc = require('utils/misc');
var Strings = require('utils/strings');
var Utils = require('models/utils');
var I18N = require('utils/i18n');

var isEthernetController = function (attributes) {
    const ethernetControllerHW = ['9', '10', '32', '34', '36'];

    attributes = attributes || this.attributes;

    var category = attributes['category'];
    var type = attributes['type'];
    var hardware = (attributes['hardware'] || '').toString();

    if (category != Consts.Category.Controller || type != '5578') {
        return false;
    }

    return ethernetControllerHW.includes(hardware);
};

var getImageName = function (attributes, small) {
    attributes = attributes || this.attributes;

    var category = attributes.category.toLowerCase();
    var type = attributes['type'];
    var hwVersion = attributes['hardware'].toString();

    var imageBaseName = '';

    switch (category) {
        case 'genericmodbus':
            return 'modbus/' + type + '.png';
        case 'meter':
        case 'fakemeter':
            return type == 'import' ? 'meter_import.png' : 'meters/' + hwVersion + '.png';
        case 'fakegateway':
            return 'unknown_large.png';
        case 'gateway':
            if (type == Consts.GatewayTypeIDs.RegComTCP) {
                imageBaseName = 'controller_5578-e';
                break;
            }
            imageBaseName = category + '_' + type;
            break;
        case '55xx56xx':
            switch (type) {
                case '5576':
                    const symbol = ['67', '99', '129', '131', '163'];
                    if (symbol.includes(hwVersion)) {
                        type += '-symbol';
                    } else if (hwVersion == '137') {
                        type += '-dhc';
                    }
                    break;
                case '5578':
                    if (isEthernetController(attributes)) {
                        type += '-e';
                    }
                    break;
            }

            imageBaseName = 'controller_' + type;
            break;
        default:
            imageBaseName = category + '_' + type;
            break;
    }

    return imageBaseName + (small ? '' : '_large') + '.png';
};

var Device = Backbone.Model.extend({
    urlRoot: '/api/v1/devices',
    defaults: {
        modelType: 'device',
    },

    initialize: function () {
        Utils.SelectableModel.applyTo(this);
    },

    isCategory: function (category) {
        return this.get('category') === category;
    },

    getCircuitNames: function () {
        var names = this.get('circuit_names');
        if (names) {
            return JSON.parse(names);
        } else {
            return Consts.DefaultCircuitNames;
        }
    },

    getLabels: function () {
        return new Labels({
            id: this.id,
            tags: this.get('tags'),
            groups: this.get('groups'),
            role: this.get('role'),
        });
    },

    getGroupFilter: function (withoutId) {
        return this.get('category') + '/' + this.get('type') + (withoutId ? '' : '/' + this.id);
    },

    getStatusDataText: function () {
        var values = this.get('values');
        if (values === undefined) {
            return undefined;
        }

        var category = this.get('category');
        var statusData = this.get('status_data');

        return _.mapObject(statusData, function (item, key) {
            switch (parseInt(key)) {
                case 1:
                    return _.map(item, function (regNr) {
                        var itemName = Misc.selectWhere(
                            values,
                            function (k) {
                                return k.type == 'MBR' && k.nr == regNr;
                            },
                            'name'
                        );

                        return I18N.DATAPOINT([category], itemName);
                    });
                default:
                    return undefined;
            }
        });
    },

    isValuesNeeded: function () {
        var statusData = this.get('status_data') || {};
        var status = this.get('status');
        return status > 0 && status & 1 && statusData['1'] && statusData['1'].length > 0;
    },

    isFirmwareUpdatePossible: function () {
        return (this.get('firmwareInfo') || {}).updatePossible || false;
    },

    getSimInfo: function (infoType) {
        var d = $.Deferred();
        var urlParams = Strings.joinURLparameter({
            infoType: encodeURIComponent(infoType),
        });

        App.ajax({
            url: '/api/v1/devices/' + this.id + '/siminfo' + urlParams,
            method: 'GET',
            success: function (result) {
                d.resolve(result);
            },
            error: function (result) {
                d.resolve(result);
            },
        });

        return d.promise();
    },

    getImageName: function (small) {
        return getImageName(this.attributes, small);
    },

    checkWritability: function (inconsistency) {
        // special treatment for SAMLAN
        this.writingDisabled =
            AppState.getDeviceInfo(this.get('parent')).category === 'Gateway-RF' && inconsistency === undefined;
    },

    isWritingDisabled: function () {
        return this.writingDisabled || false;
    },

    isExternalDevice: function () {
        return (this.get('type') || '').startsWith('EXT_');
    },

    isEthernetController: function () {
        return isEthernetController(this.attributes);
    },

    isDirectlyConnected: function () {
        return this.get('wired_to') == this.id;
    },

    canShowScheme: function () {
        var deviceHasNecessaryFields = this.attributes.firmware && this.attributes.category;
        return deviceHasNecessaryFields && this.get('category') === '55xx56xx';
    },
});

var DeviceListResult = Backbone.Model.extend({
    initialize: function (options) {
        this.options = options;

        this.filter = options.filter || {};
        if (typeof this.filter.tags === 'string') {
            this.filter.tags = [options.filter.tags];
        }
        this.urlSuffix = options.urlSuffix || '';
        this.filter.conditions = this.filter.conditions || [];
        this.filterParams = options.filterParams || {};
        this.filter.bbox = this.filterParams.bbox || '';
        this.columns = options.columns || [];
        this.wantAll = options.wantAll;
        this.cache = options.cache ? 'yes' : undefined;
        this.isFilter = options.isFilter;
        this.addr = options.addr;

        if (this.filter && this.filter.device_groups) {
            this.filter.device_groups = Misc.parseStrIntArray(this.filter.device_groups);
        }
        if (this.filter && this.filter.device_groups_excluded) {
            this.filter.device_groups_excluded = Misc.parseStrIntArray(this.filter.device_groups_excluded);
        }

        this.connectable = encodeURIComponent(this.filterParams.connectable || '');
        this.connectionDeviceID = encodeURIComponent(this.filterParams.connectionDeviceID || '');

        this.parentID = encodeURIComponent(this.filterParams.parentID || '');
        this.mode = encodeURIComponent(this.filterParams.mode || '');
        this.permission = encodeURIComponent(this.filterParams.permission || '');
        this.limit = encodeURIComponent(this.filterParams.limit || 0);
        this.macAddress = encodeURIComponent(this.filterParams.macAddress || '');
        this.countOnly = encodeURIComponent(this.filterParams.countOnly || false);
        this.wantAll = encodeURIComponent(this.wantAll ? true : false);
        this.columns = encodeURIComponent(this.columns.join(','));
        this.addr = encodeURIComponent(this.addr || '');

        if (this.filterParams) {
            if (this.filterParams.page && this.filterParams.limit) {
                this.offset = (this.filterParams.page - 1) * this.filterParams.limit;
            }
            this.sortCol = this.sortCol || this.filterParams.sortCol;
            this.sortDesc = this.sortDesc || this.filterParams.sortDesc;
        }
        if (this.filterParams.type) this.filter.type = _.flatten([this.filterParams.type]);
        if (this.filterParams.category) this.filter.category = _.flatten([this.filterParams.category]);
    },
    url: function () {
        var urlParams = Strings.joinURLparameter({
            filter: Misc.filterToJSON(this.filter),
            parent: this.parentID,
            wired_to: this.connectionDeviceID,
            mac: this.macAddress,
            mode: this.mode,
            permission: this.permission,
            limit: this.limit,
            offset: this.offset,
            sort_col: Misc.correctSortCol(this.sortCol),
            sort_desc: this.sortDesc,
            count_only: this.countOnly,
            columns: this.columns,
            wantall: this.wantAll,
            cache: this.cache,
            caller: this.isFilter ? 'filter' : '',
            addr: this.addr,
            connectable: this.connectable,
        });

        this.lastURL = '/api/v1/devices' + this.urlSuffix + urlParams;
        return this.lastURL;
    },
    setPage: function (page, itemsPerPage, sortCol, sortDesc, onComplete) {
        var self = this;

        this.offset = (page - 1) * itemsPerPage;
        this.limit = itemsPerPage;
        this.sortCol = sortCol;
        this.sortDesc = sortDesc;
        this.cache = 'yes';
        this.fetch({
            success: function (data, response) {
                var items = new DeviceCollection(self.get('items'));
                var count = self.get('count');
                devicePostProcess({
                    source: items.models,
                    collection: items,
                    parentDevices: self.get('parent'),
                    formatInfos: response.format_infos,

                    onDone: function () {
                        if (onComplete) onComplete(items, count);
                    },
                });
            },
        });
    },
});

var DeviceCollection = Backbone.Collection.extend({
    model: Device,
    urlRoot: '/api/v1/devices',
    initialize: function (options) {
        Utils.SelectableCollection.applyTo(this, options);
    },
    getSelectedCollection: function (idList) {
        var list = Misc.selectWhere(_.pairs(this.selected), undefined, function (item) {
            return item[1];
        });

        return new DeviceCollection(list);
    },
});

var EventLogItemCollection = Backbone.Collection.extend({
    initialize: function (models, options) {
        this.count = options.count;
    },
});

var DeviceScan = Backbone.Model.extend({
    defaults: {
        modelType: 'deviceScan',
    },

    initialize: function (options) {
        this.device = options.device;
    },

    url: function () {
        return '/api/v1/devices/' + this.device + '/scan/modbus/' + this.id;
    },
});

function getDeviceNameList(devices, getLocations) {
    var batchSize = 500;
    var restData = undefined;

    if (devices.length > batchSize) {
        restData = devices.slice(batchSize, devices.length);
        devices = devices.slice(0, batchSize);
    }

    var query = '?devices=' + encodeURIComponent((devices || []).join(','));
    query += getLocations ? '&locations=true' : '';

    var defer = $.Deferred();
    App.ajax({
        method: 'GET',
        url: '/api/v1/devices/names' + query,
        success: function (result) {
            AppState.registerDevices(result.items);
            if (restData && restData.length) {
                var r = getDeviceNameList(restData);
                $.when(r).done(function (restResult) {
                    return defer.resolve(_.flatten([result.items, restResult]));
                });

                return;
            }

            defer.resolve(result.items);
        },
    });
    return defer.promise();
}

function devicePostProcess(callCtx) {
    if (callCtx.collection) {
        callCtx.data = callCtx.collection.toJSON();
    }

    AppState.requireColumns(function () {
        // register current devices
        AppState.registerDevices(callCtx.data);
        if (callCtx.parentDevices) {
            AppState.registerDevices(callCtx.parentDevices);
        }
        _.each(callCtx.source, function (m) {
            m.set('tags', m.get('tags') || []);
            m.set('groups', m.get('groups') || []);
            switch (m.get('category')) {
                case Consts.CategoryData.Virtual.category:
                    var f = m.get('filter');
                    if (f) {
                        m.set('filter_text', AppState.filterToStr(f));
                    }
                    break;
            }
        });

        if (callCtx.collection && callCtx.collection.models && callCtx.formatInfos) {
            // "uncompress" format infos -> assign to every collection device
            _.each(callCtx.collection.models, function (device) {
                // special handling: format id specified
                if (device.attributes.format_id) {
                    // .. use direct lookup
                    device.set('formatInfos', callCtx.formatInfos[device.attributes.format_id]);
                    return;
                }

                var deviceFormatInfos = {};

                // determine format for all device data fields
                _.each(device.attributes.data, function (value, key) {
                    var formatInfo = callCtx.formatInfos[key];
                    if (formatInfo === undefined) {
                        // skip -> no format info available
                        return;
                    }

                    if (formatInfo.length === 1) {
                        // no device specific formats -> use for current device
                        deviceFormatInfos[key] = formatInfo[0];
                    } else {
                        // search for device specific format
                        var deviceFormat = undefined;
                        var defaultFormat = _.filter(formatInfo, function (e) {
                            return e.devices === undefined;
                        })[0];

                        _.each(formatInfo, function (e) {
                            if (_.indexOf(e.devices, device.id) != -1) {
                                deviceFormat = e;
                            }
                        });

                        deviceFormatInfos[key] = deviceFormat || defaultFormat;

                        // ensure valid unit/display_unit
                        deviceFormatInfos[key].display_unit = deviceFormatInfos[key].display_unit || '';
                    }
                });

                device.set('formatInfos', deviceFormatInfos);
            });
        }

        var parentMap = {};
        _.each(callCtx.data, function (item) {
            parentMap[item.parent] = true;
        });
        var parentList = _.keys(parentMap);
        _.each(parentList, function (item, idx) {
            parentList[idx] = parseInt(item);
        });
        // registe parent devices
        var parentsToRegister = Misc.selectWhere(parentList, function (item) {
            return item && !AppState.isDeviceRegistered(item);
        });

        if (parentsToRegister.length) {
            var req = getDeviceNameList(parentsToRegister);
            $.when(req).done(function (result) {
                AppState.registerDevices(result);
                callCtx.onDone();
            });
        } else {
            callCtx.onDone();
        }
    });
}

function checkAvailColumns(callCtx) {
    App.request('user:columns', callCtx);
}

function getDeviceSummaryRequest(type) {
    switch (type) {
        case 'profile':
            return {
                features: 'category,status',
                count: 'id',
                filter: '{}',
            };
    }
}

function summarizeByStatus(devices) {
    var result = [];
    var categories = {};
    var prio = {
        '-1': 1,
        '-3': -1,
        '-2': -2,
        0: -3,
    };

    _.each(devices, function (row) {
        categories[row.features.category] = categories[row.features.category] || row;
        var current = categories[row.features.category];
        if (current != row) {
            current.stats.id.count += row.stats.id.count;
        } else {
            result.push(current);
        }

        if ((prio[row.features.status] || 0) > (prio[current.features.status] || 0)) {
            current.features.status = row.features.status;
        }
    });
    return result;
}

var API = {
    getDeviceSummary: function (type) {
        var urlParams = Strings.joinURLparameter(getDeviceSummaryRequest(type));
        return Misc.deferredFct(function (defer) {
            App.ajax({
                method: 'GET',
                url: '/api/v1/devices/summary' + urlParams,
                success: function (data) {
                    defer.resolve(summarizeByStatus(data));
                },
                error: function () {
                    defer.resolve(undefined);
                },
            });
        });
    },

    updateSelection: function (selectionChange) {
        var defer = $.Deferred();
        App.ajax({
            method: 'POST',
            url: '/api/v1/devices/selection',
            data: JSON.stringify(selectionChange),
            success: function (result) {
                defer.resolve(result);
            },
            error: function () {
                defer.resolve(undefined);
            },
        });
        return defer.promise();
    },

    getDeviceNameList: function (filter, from, count) {
        var defer = $.Deferred();

        var url = '';
        url += 'name=' + filter.name;
        url += filter.categories ? '&categories=' + encodeURIComponent(filter.categories.join(',')) : '';
        url += filter.connectable ? '&connectable=true' : '';
        url += filter.status ? '&status=' + encodeURIComponent(filter.status.join(',')) : '';
        url += filter.parent ? '&parent=' + encodeURIComponent(filter.parent) : '';
        url += filter.capabilities ? '&get_capabilities=true' : '';
        url += filter.locations ? '&locations=true' : '';

        App.ajax({
            method: 'GET',
            url: '/api/v1/devices/names?' + url + '&from=' + from + '&count=' + count,
            success: function (reply) {
                AppState.registerDevices(reply.items);
                defer.resolve(reply);
            },
            error: function () {
                defer.resolve([]);
            },
        });
        return defer.promise();
    },

    getMultipleDeviceRegisters: function (deviceRegisterMap, nonPollingValues) {
        nonPollingValues = nonPollingValues || {};

        // pre-processing: move invalid-polling registers to 'nonPollingValues'
        _.each(deviceRegisterMap, function (registerList, deviceID) {
            var partitions = _.partition(registerList, function (r) {
                return r.startsWith('MB');
            });

            // re-assign all valid datapoints
            deviceRegisterMap[deviceID] = partitions[0];
            // move invalid data points to non-polling
            if (partitions[1].length > 0) {
                nonPollingValues[deviceID] = _.union(nonPollingValues[deviceID] || [], partitions[1]);
            }
        });

        var d = $.Deferred();
        App.ajax({
            method: 'POST',
            url: '/api/v1/devices/values',
            data: JSON.stringify({ values: deviceRegisterMap, values_non_polling: nonPollingValues }),
            success: function (result) {
                d.resolve(result);
            },
            error: function (result) {
                d.resolve(result);
            },
        });

        return d.promise();
    },

    getDeviceNames: function (devices, getLocation) {
        var defer = $.Deferred();

        // select unregistered devices
        var devicesToLoad = Misc.selectWhere(devices, function (id) {
            var isRegistered = AppState.isDeviceRegistered(id);
            var hasLocation = isRegistered && AppState.getDeviceInfo(id).location;
            return getLocation ? !hasLocation : !isRegistered;
        });

        if (devicesToLoad.length) {
            var req = getDeviceNameList(devicesToLoad, getLocation);
            $.when(req).done(function (result) {
                AppState.registerDevices(result);

                // add all registrations for devices
                defer.resolve(
                    _.map(devices, function (id) {
                        return AppState.getDeviceInfo(id);
                    })
                );
                return;
            });
            return req.promise();
        } else {
            setTimeout(function () {
                defer.resolve(
                    _.map(devices, function (id) {
                        return AppState.getDeviceInfo(id);
                    })
                );
                return;
            });
        }

        return defer.promise();
    },

    getDeviceList: function (listSettings, urlSuffix) {
        var filterParams = listSettings.filterParams || {};
        var includeID = listSettings.includeID;
        var filter = listSettings.filter;
        var columns = listSettings.columns;
        var wantAll = listSettings.wantAll;
        var addr = listSettings.addr;
        var onError = listSettings.onError;

        var deviceListResult = new DeviceListResult({
            urlSuffix: urlSuffix,
            filterParams: filterParams,
            filter: filter || filterParams.filter,
            columns: columns,
            wantAll: wantAll,
            cache: listSettings.cache,
            addr: addr,
        });
        var defer = $.Deferred();

        deviceListResult.fetch({
            success: function (data, response) {
                var origResult = data;
                data = data.toJSON();
                var collection = new DeviceCollection(data.items);

                var resolveResult = function () {
                    var done = function () {
                        return defer.resolve({
                            items: collection,
                            count: data.count,
                            listResult: origResult,
                            url: deviceListResult.lastURL,
                        });
                    };

                    devicePostProcess({
                        collection: collection,
                        onDone: done,
                        parentDevices: data.parent,
                        formatInfos: response.format_infos,
                    });
                };

                if (includeID) {
                    // is the included device found in the list?
                    var list = collection.toJSON();
                    var found = Misc.selectWhereFirst(list, function (a) {
                        return a.id == includeID;
                    });
                    if (!found) {
                        var d = new Device({ id: includeID });
                        d.fetch({
                            success: function (includedDeviceData) {
                                collection.add(includedDeviceData);
                                data.count++;
                                resolveResult();
                            },
                            error: function () {
                                resolveResult();
                            },
                        });
                        return;
                    }
                }

                resolveResult();
            },
            error: function (data, response) {
                switch (onError) {
                    case 'JSON':
                        defer.resolve(response.responseJSON);
                    case 'emptyList':
                        defer.resolve(new DeviceCollection([]));
                    default:
                        defer.resolve(undefined);
                }
            },
        });

        return defer.promise();
    },

    getDevice: function (id) {
        var device = new Device({ id: id });
        var defer = $.Deferred();
        var self = this;

        device.fetch({
            success: function (data, response) {
                devicePostProcess({
                    source: [data],
                    data: [data.toJSON()],
                    onDone: function () {
                        defer.resolve(data);
                    },
                });
            },
            error: function (data) {
                defer.resolve(undefined);
            },
        });

        return defer.promise();
    },

    getDeviceEventLog: function (filterParams, id) {
        var defer = $.Deferred();
        var deviceID = id;
        filterParams = filterParams || {};
        var eventType = filterParams.eventType || [];
        var itemCount = filterParams.itemCount || 20;
        var currentPage = filterParams.currentPage || 1;
        var timeFrom = filterParams.timeFrom || '';
        var timeTill = filterParams.timeTill || '';
        var dateFormatReq = 'YYYY-MM-DD HH:mm:ss';

        if (moment.isMoment(timeFrom)) {
            timeFrom = moment(timeFrom).utc();
            timeFrom = timeFrom.format(dateFormatReq) + ' UTC';
        }
        if (moment.isMoment(timeTill)) {
            timeTill = moment(timeTill).utc();
            timeTill = timeTill.format(dateFormatReq) + ' UTC';
        }

        var offset = (currentPage - 1) * itemCount;
        var limit = itemCount;
        var urlParams = Strings.joinURLparameter({
            itemtypes: encodeURIComponent(eventType),
            offset: encodeURIComponent(offset),
            limit: encodeURIComponent(limit),
            from: encodeURIComponent(timeFrom),
            till: encodeURIComponent(timeTill),
        });
        var urlStr = '/api/v1/devices/' + deviceID + '/eventlog' + urlParams;

        App.ajax({
            method: 'GET',
            url: urlStr,
            success: function (data) {
                defer.resolve(new EventLogItemCollection(data.items, { count: data.count }));
            },
            error: function (data) {
                defer.resolve(undefined);
            },
        });

        return defer.promise();
    },

    scanController: function (device, unit, detectTypes) {
        var defer = $.Deferred();

        var register = new DeviceScan({ id: unit, device: device });
        register.fetch({
            success: function (data) {
                defer.resolve(data);
            },
            error: function (data) {
                defer.resolve(undefined);
            },
        });

        return defer.promise();
    },

    scanSamlanInitial: function (device) {
        var url = '/api/v1/devices/' + device + '/scan/samlan';
        var defer = $.Deferred();

        App.ajax({
            method: 'GET',
            url: url,
            success: function (data) {
                defer.resolve(data);
            },
            error: function (data) {
                defer.resolve(undefined);
            },
        });

        return defer.promise();
    },

    scanMeter: function (device, unit) {
        var url = '/api/v1/devices/' + device + '/scan/mbus';
        if (unit) {
            url = url + '/' + unit.idx;
        }

        var defer = $.Deferred();

        App.ajax({
            method: 'GET',
            url: url,
            success: function (data) {
                defer.resolve(data);
            },
            error: function (data) {
                defer.resolve(undefined);
            },
        });

        return defer;
    },

    getMeterEopInfos: function (device) {
        var defer = $.Deferred();
        App.ajax({
            method: 'GET',
            url: '/api/v1/devices/' + device + '/eopvalues',
            success: function (data) {
                defer.resolve(data);
            },
            error: function (data) {
                defer.resolve(undefined);
            },
        });

        return defer;
    },

    getDeviceColumns: function () {
        var defer = $.Deferred();
        App.ajax({
            method: 'GET',
            url: '/api/v1/devices/columns',
            success: function (data) {
                return defer.resolve(data);
            },
            error: function (data) {
                return defer.resolve();
            },
        });
        return defer.promise();
    },

    getDeviceBounds: function () {
        var defer = $.Deferred();
        App.ajax({
            method: 'GET',
            url: '/api/v1/devices_geo/bounds',
            success: function (data) {
                return defer.resolve(data);
            },
            error: function (data) {
                return defer.resolve();
            },
        });
        return defer.promise();
    },

    deviceReplacement: function (replaced_id, new_id, mode) {
        var defer = $.Deferred();
        App.ajax({
            method: 'POST',
            url: '/api/v1/devices/' + replaced_id + '/replace',
            data: JSON.stringify({ replacement_id: new_id, mode: mode }),

            success: function (data) {
                return defer.resolve(data);
            },
            error: function (data) {
                return defer.resolve(undefined);
            },
        });
        return defer.promise();
    },

    deviceInconsistency: function (device) {
        var defer = $.Deferred();
        App.ajax({
            method: 'GET',
            url: '/api/v1/devices/' + device.id + '/inconsistency',
            success: function (data) {
                var inconsistency = data || {};
                device.checkWritability(inconsistency);
                return defer.resolve(inconsistency);
            },
            error: function (data) {
                device.checkWritability(undefined);
                return defer.resolve();
            },
        });
        return defer.promise();
    },

    deviceSensorValues: function (deviceID) {
        var defer = $.Deferred();
        App.ajax({
            method: 'GET',
            url: '/api/v1/devices/' + deviceID + '/sensorvalues',
            success: function (result) {
                defer.resolve(result);
            },
            error: function (result) {
                defer.resolve('');
            },
        });
        return defer.promise();
    },

    getControllerBackups: function (deviceType) {
        var defer = $.Deferred();
        App.ajax({
            method: 'GET',
            url: '/api/v1/controller-backups' + (deviceType ? '?type=' + deviceType : ''),
            success: function (result) {
                defer.resolve(result);
            },
            error: function (result) {
                defer.reject();
            },
        });
        return defer.promise();
    },

    getDeviceParameter: function (deviceID, modernFirmware) {
        var defer = $.Deferred();
        App.ajax({
            method: 'GET',
            url: '/api/v1/devices/' + deviceID + '/parameter?modernFirmware=' + modernFirmware,
            success: function (parameter) {
                defer.resolve(parameter);
            },
            error: function (result) {
                defer.resolve(result.responseJSON);
            },
        });
        return defer.promise();
    },

    setDeviceParameter: function (deviceID, dataPoints) {
        var defer = $.Deferred();
        App.ajax({
            method: 'POST',
            data: JSON.stringify(dataPoints),
            url: '/api/v1/devices/' + deviceID + '/parameter',
            success: function () {
                defer.resolve(true);
            },
            error: function (result) {
                defer.resolve(false);
            },
        });
        return defer.promise();
    },

    getOrUpdateSchemeConfig: function (deviceIDs, schemeConfig) {
        var defer = $.Deferred();
        var request = {
            method: 'GET',
            url: '/api/v1/devices/scheme-config?device_ids=' + deviceIDs.join(','),
            success: function (response) {
                defer.resolve(response);
            },
            error: function () {
                defer.resolve(false);
            },
        };
        if (schemeConfig != undefined) {
            request.method = schemeConfig.length === 0 ? 'DELETE' : 'POST';
            request.data = JSON.stringify(schemeConfig);
        }
        App.ajax(request);
        return defer.promise();
    },

    getOrUpdateHCsConfig: function (controllerID, meterID, newSelection) {
        var defer = $.Deferred();
        var request = {
            method: 'GET',
            url:
                '/api/v1/smart-temp/hcs-config?controller_id=' + controllerID + (meterID ? '&meter_id=' + meterID : ''),
            success: function (response) {
                defer.resolve(response);
            },
            error: function () {
                defer.resolve(false);
            },
        };
        if (newSelection) {
            request.method = newSelection.length === 0 ? 'DELETE' : 'PUT';
            request.contentType = 'application/json; charset=utf-8';
            request.data = JSON.stringify(newSelection);
        }
        App.ajax(request);
        return defer.promise();
    },

    getFirmwareUpdateInformation: function (deviceID) {
        var defer = $.Deferred();
        App.ajax({
            method: 'GET',
            url: '/api/v1/devices/' + deviceID + '/firmware',
            success: function (firmwareInfo) {
                defer.resolve(firmwareInfo);
            },
            error: function (result) {
                defer.resolve(undefined);
            },
        });
        return defer.promise();
    },
    setDeviceContractNr: function (deviceID, contractNr) {
        return Misc.deferredFct(function (defer) {
            App.ajax({
                method: 'PUT',
                url: '/api/v1/devices/' + deviceID + '/contract',
                data: JSON.stringify({ contract_nr: '' + contractNr }),
                success: function (r) {
                    defer.resolve(true);
                },
                error: function (r) {
                    defer.resolve(false);
                },
            });
        });
    },
    getDeviceContractNr: function (deviceID) {
        return Misc.deferredFct(function (defer) {
            App.ajax({
                method: 'GET',
                url: '/api/v1/devices/' + deviceID + '/contract',
                success: function (r) {
                    defer.resolve(r);
                },
                error: function (r) {
                    defer.resolve(null);
                },
            });
        });
    },
    getDeviceFunctionBlockGroups: function (deviceID) {
        return Misc.deferredFct(function (defer) {
            App.ajax({
                method: 'GET',
                url: '/api/v1/devices/' + deviceID + '/fb',
                success: function (r) {
                    defer.resolve(r);
                },
                error: function (r) {
                    defer.resolve(null);
                },
            });
        });
    },
    getDeviceFunctionsBlocks: function (deviceID, wantActive, group) {
        return Misc.deferredFct(function (defer) {
            App.ajax({
                method: 'GET',
                url: '/api/v1/devices/' + deviceID + '/fb?' + (wantActive ? 'active=true' : 'group=' + group),
                success: function (r) {
                    defer.resolve(r);
                },
                error: function (r) {
                    defer.resolve(null);
                },
            });
        });
    },
    setDeviceFunctionBlock: function (deviceID, group, block, active) {
        return Misc.deferredFct(function (defer) {
            App.ajax({
                contentType: 'application/json',
                method: 'POST',
                url: '/api/v1/devices/' + deviceID + '/fb',
                data: JSON.stringify({
                    group: group,
                    block: block,
                    is_active: active,
                }),
                success: function (r) {
                    defer.resolve(r);
                },
                error: function (r) {
                    defer.resolve(null);
                },
            });
        });
    },
};

App.reqres.setHandler('device:list', function (listSettings) {
    return API.getDeviceList(listSettings || {});
});

App.reqres.setHandler('device:list:geo', function (listSettings) {
    return API.getDeviceList(listSettings || {}, '_geo');
});

// create list requests by category
var buildListFunction = function (category) {
    return function (filterParams) {
        return API.getDeviceList({
            filterParams: $.extend({ category: category }, filterParams),
        });
    };
};

App.reqres.setHandler('device:model', function (id) {
    return API.getDevice(id);
});

App.reqres.setHandler('device:eventlog', function (filterParams, id) {
    return API.getDeviceEventLog(filterParams || {}, id);
});

App.reqres.setHandler('controller:scan', function (device, unit) {
    return API.scanController(device, unit);
});

App.reqres.setHandler('controller:scan:samlan', function (device) {
    return API.scanSamlanInitial(device);
});

App.reqres.setHandler('meter:scan', function (device, unit) {
    return API.scanMeter(device, unit);
});

App.reqres.setHandler('meter:eop_infos', function (deviceID) {
    return API.getMeterEopInfos(deviceID);
});

App.reqres.setHandler('devices:selection', function (selectionChange) {
    return API.updateSelection(selectionChange);
});

App.reqres.setHandler('device:columns', function () {
    return API.getDeviceColumns();
});

App.reqres.setHandler('device:names', function (devices, getLocation) {
    return API.getDeviceNames(devices, getLocation);
});

App.reqres.setHandler('devices:registers', function (deviceRegisterMap, nonPollingValues) {
    return API.getMultipleDeviceRegisters(deviceRegisterMap, nonPollingValues);
});

App.reqres.setHandler('device:name:list', function (filter, from, count) {
    return API.getDeviceNameList(filter, from, count);
});

App.reqres.setHandler('device:summary', function (type) {
    return API.getDeviceSummary(type);
});

App.reqres.setHandler('device:replacement', function (replaced_id, new_id, mode) {
    return API.deviceReplacement(replaced_id, new_id, mode);
});

App.reqres.setHandler('device:inconsistent', function (device) {
    return API.deviceInconsistency(device);
});

App.reqres.setHandler('device:sensors', function (deviceID) {
    return API.deviceSensorValues(deviceID);
});

App.reqres.setHandler('devices:backups', function (deviceType) {
    return API.getControllerBackups(deviceType);
});

App.reqres.setHandler('device:get:parameter', function (deviceID, modernFirmware) {
    return API.getDeviceParameter(deviceID, modernFirmware);
});

App.reqres.setHandler('device:set:parameter', function (deviceID, dataPoints) {
    return API.setDeviceParameter(deviceID, dataPoints);
});

App.reqres.setHandler('device:scheme-config', function (deviceIDs, schemeConfig) {
    return API.getOrUpdateSchemeConfig(deviceIDs, schemeConfig);
});
App.reqres.setHandler('device:hcs-config', function (controllerID, meterID, newSelection) {
    return API.getOrUpdateHCsConfig(controllerID, meterID, newSelection);
});

App.reqres.setHandler('device:bounds', function () {
    return API.getDeviceBounds();
});

App.reqres.setHandler('device:firmwareUpdate', function (deviceID) {
    return API.getFirmwareUpdateInformation(deviceID);
});

App.reqres.setHandler('device:contract:set', function (deviceID, contractNr) {
    return API.setDeviceContractNr(deviceID, contractNr);
});

App.reqres.setHandler('device:contract:get', function (deviceID) {
    return API.getDeviceContractNr(deviceID);
});

App.reqres.setHandler('device:fcb:groups', function (deviceID) {
    return API.getDeviceFunctionBlockGroups(deviceID);
});

App.reqres.setHandler('device:fcb:blocks', function (deviceID, wantActive, group) {
    return API.getDeviceFunctionsBlocks(deviceID, wantActive, group);
});

App.reqres.setHandler('device:fcb:change', function (deviceID, group, block, active) {
    return API.setDeviceFunctionBlock(deviceID, group, block, active);
});

module.exports = {
    Model: Device,
    Collection: DeviceCollection,

    InitDeviceLists: function () {
        _.each(Consts.DeviceCategory, function (data, category) {
            App.reqres.setHandler(category + ':list', buildListFunction(category));
        });
    },

    IsEthernetController: isEthernetController,
    GetImageName: getImageName,
};
